import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import Swal from "sweetalert2";
import moment from "moment";

//import components
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer5";
import { Nav, NavItem, NavLink } from "reactstrap";

const DatatableTables = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const getFormattedDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const todayDateObj = new Date();
  const yesterdayDateObj = new Date();
  yesterdayDateObj.setDate(todayDateObj.getDate() - 1);
  const todayFormatted = getFormattedDate(todayDateObj);
  const yesterdayFormatted = getFormattedDate(yesterdayDateObj);
  const [selectedDayTab, setSelectedDayTab] = useState("today");
  const [isClassCancelled, setIsClassCancelled] = useState(false);
  const [classDate, setClassDate] = useState(todayFormatted);
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlot, setTimeSlot] = useState("");
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Asc");
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchData = useCallback(async () => {
    try {
      if (!classDate || !timeSlot) return;

      setLoading(true);

      const query = new URLSearchParams({
        classDate,
        timeSlot,
        page,
        countPerPage,
        sortingBy,
        direction,
      }).toString();

      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetStudentsAttendanceList?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);

      const cancelled = result?.recordsets?.[2]?.[0]?.IsClassCancelled === 1;
      setIsClassCancelled(cancelled);
    } catch (error) {
      console.error("Error loading attendance list:", error);
    } finally {
      setLoading(false);
    }
  }, [classDate, page, timeSlot, countPerPage, sortingBy, direction, token]);

  const fetchBatchSlots = useCallback(async (selectedDate) => {
    try {
      const dayName = moment(selectedDate).format("dddd");

      const res = await fetch(
        `https://api.kunalsdrumclass.com/api/GetBatchSlotsByDay?day=${dayName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();

      const slots = json?.recordsets?.[0] || [];

      setTimeSlots(slots);

      if (slots.length > 0) {
        setTimeSlot(slots[0].Batch_Time);
      } else {
        setTimeSlot("");
      }

    } catch (error) {
      console.error("Batch slot API error:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchBatchSlots(classDate);
  }, [classDate, fetchBatchSlots]);

  useEffect(() => {

    if (selectedDayTab === "yesterday") {
      fetchData();
      return;
    }

    if (isTodaySlotVisible()) {
      fetchData();
    }

  }, [fetchData, selectedDayTab, classDate, timeSlot, page, countPerPage, sortingBy, direction]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleTimeSlotChange = (time) => {
    setTableData([]);
    setTotalRecords(0);
    setTimeSlot(time);
    setPage(1);
  };

  const handleDayTabChange = (tab) => {
    setTableData([]);
    setTotalRecords(0);
    setSelectedDayTab(tab);

    const selectedDate =
      tab === "today"
        ? todayDateObj
        : yesterdayDateObj;

    setClassDate(getFormattedDate(selectedDate));
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const handleAttendance = useCallback(async (rowData, status) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to mark this student as ${status.toLowerCase()} ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    try {
      await fetch(
        "https://api.kunalsdrumclass.com/api/InsertStudentAttendance",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            studentId: rowData.Student_Id,
            classDate: rowData.Class_Date,
            timeSlot: rowData.Time_Slot,
            status: status,
            createdBy: user.Id,
          }),
        }
      );

      await Swal.fire({
        title: "Success!",
        text: `Student attendance updated successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      await fetchData();
    } catch {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  }, [token, user?.Id, fetchData]);

  const columns = useMemo(
    () => [
      {
        header: "Name",
        meta: {
          sticky: true,
          width: "125px",
        },
        accessorKey: "Student_Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Contact No",
        accessorKey: "Contact_Number",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const mobile = rowData.Contact_Number;
          const cleanNumber = mobile.replace(/\D/g, "");
          const email = rowData.Email_Address;

          return (
            <div className="d-flex flex-column gap-1">
              <span>{rowData.Contact_Number}</span>
              <div className="d-flex gap-2 align-items-center">
                <a
                  href={`tel:+${cleanNumber}`}
                  title="Call"
                >
                  <i className="mdi mdi-phone-outline fs-3"></i>
                </a>
                <a
                  href={`https://wa.me/${cleanNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp fs-3"></i>
                </a>
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? `mailto:${email}`
                      : `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Send Email"
                >
                  <i className="mdi mdi-email-send-outline fs-3"></i>
                </a>

              </div>
            </div>
          );
        },
      },
      {
        header: "Class Type",
        accessorKey: "Booking_Type",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Attendance Time",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value ? moment(value).format("DD-MM-YYYY hh:mm A") : "Not Taken Yet";
        },
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const status = rowData.Attendance_Status;
          const modifiedOn = rowData.ModifiedOn;
          const canReverse = modifiedOn === null || modifiedOn === undefined;

          return (
            <div className="d-flex gap-2">

              {(!status || status === null) && (
                <>
                  {hasPermission(permissionMap, MODULES.ATTENDANCE, PERMISSIONS.EDIT) && (
                    <button className="btn btn-sm btn-success" onClick={() => handleAttendance(rowData, "Present")}>
                      Present
                    </button>
                  )}
                  {hasPermission(permissionMap, MODULES.ATTENDANCE, PERMISSIONS.EDIT) && (
                    <button className="btn btn-sm btn-danger" onClick={() => handleAttendance(rowData, "Absent")}>
                      Absent
                    </button>
                  )}
                </>
              )}

              {status === "Present" && (
                <button className="btn btn-sm btn-success">Present</button>
              )}

              {status === "Absent" && (
                <button className="btn btn-sm btn-danger">Absent</button>
              )}
            </div>
          );
        }
      },
      {
        header: "Reversal",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const status = rowData.Attendance_Status;
          const modifiedOn = rowData.ModifiedOn;
          const canReverse = modifiedOn === null || modifiedOn === undefined;

          return (
            <div className="d-flex gap-2">

              {status === "Present" && (
                <>
                  {canReverse && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleAttendance(rowData, "Absent")}>
                      Mark Absent
                    </button>
                  )}
                </>
              )}

              {status === "Absent" && (
                <>
                  {canReverse && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleAttendance(rowData, "Present")}>
                      Mark Present
                    </button>
                  )}
                </>
              )}

            </div>
          );
        }
      }
    ],
    [permissionMap, handleAttendance]
  );

  const isTodaySlotVisible = () => {
    if (selectedDayTab === "yesterday") {
      return true;
    }

    if (!timeSlot) return false;

    const startTime = timeSlot.split("-")[0].trim();
    const now = moment();
    const slotStart = moment(
      `${classDate} ${startTime}`,
      "YYYY-MM-DD hh:mmA"
    );
    const visibleTime = slotStart.clone().subtract(30, "minutes");

    return now.isSameOrAfter(visibleTime);
  };

  const canShowAttendance = isTodaySlotVisible();
  const currentDateObj = selectedDayTab === "today" ? todayDateObj : yesterdayDateObj;
  const currentDayName = currentDateObj.toLocaleDateString("en-US", {
    weekday: "long",
  });

  //meta title
  document.title = "Students Attendance List | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Students Attendance List"
          breadcrumbItems={[
            { title: "Students Attendance List" }
          ]}
        />

        <div className="d-flex gap-2 mb-3">
          <div className="mb-3">
            <Nav tabs>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedDayTab === "today",
                  })}
                  onClick={() => handleDayTabChange("today")}
                >
                  Today
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedDayTab === "yesterday",
                  })}
                  onClick={() => handleDayTabChange("yesterday")}
                >
                  Yesterday
                </NavLink>
              </NavItem>
            </Nav>
          </div>
        </div>

        <div className="mb-3">
          <h5 className="mb-0">
            {selectedDayTab === "today"
              ? "Today's"
              : "Yesterday's"}{" "}
            Attendance
          </h5>
        </div>

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          classDate={classDate}
          timeSlots={timeSlots}
          timeSlot={timeSlot}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          isClassCancelled={isClassCancelled}
          canShowAttendance={canShowAttendance}
          onPageChange={handlePageChange}
          onTimeSlotChange={handleTimeSlotChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          isLoading={loading}
          isPagination
          isCustomPageSize
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

      </div>
    </div>
  );
};

export default DatatableTables;
